// import Clarity from '@microsoft/clarity';
import LocationSyncModal from "components/LocationSyncModal";
import { LocationSessionProvider } from "components/location/LocationSessionProvider";
import { ModalsProvider } from "components/modals";
import React, { lazy, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import {
  AUDIT_TRAIL_ACCESS,
  CENTRAL_STORE_ACCESS,
  hasAnyPermission,
  hasAnySettingsAccess,
  MODULE_IDS,
  REPORT_ACCESS,
  SUBSCRIPTIONS_ACCESS,
} from "config/access-control";
import MaintenanceGate from "./MaintenanceGate";
import { AuthProvider, useAuth } from "./AuthProvider";
import { goToLogin } from "services/session";

const PublicRoute = lazy(() => import("routes/PublicRoute"));
const ModuleRoutes = lazy(() => import("routes/ModuleRoutes"));
const AuditTrailRoutes = lazy(() => import("modules/audit-trail"));
const LaboratoryRoutes = lazy(() => import("modules/laboratory"));
const SettingsRoutes = lazy(() => import("modules/settings"));
const InventoryRoutes = lazy(() => import("modules/inventory"));
const OnboardingRoutes = lazy(() => import("modules/onboarding"));
const FrontdeskRoutes = lazy(() => import("modules/front-desk"));
const PatientRoutes = lazy(() => import("modules/patients"));
const PharmacyRoutes = lazy(() => import("modules/pharmacy"));
const FinanceRoutes = lazy(() => import("modules/finance"));
const ReportingRoutes = lazy(() => import("modules/reporting"));
const SubscriptionRoutes = lazy(() => import("modules/subscriptions"));

const ModuleRoute = ({ module, parentPermissions, component: Component, ...rest }) => {
  const current = useSelector((state) => state.user?.current);

  return (
    <Route
      {...rest}
      render={(routeProps) => {
        if (current?.parent) {
          const accessGranted = parentPermissions?.length && hasAnyPermission(current.permissions || [], parentPermissions);
          return accessGranted ? <Component {...routeProps} /> : <Redirect to="/modules" />;
        }
        if (!Array.isArray(current?.modules)) return <Redirect to="/modules" />;
        return current.modules.includes(module) ? <Component {...routeProps} /> : <Redirect to="/modules" />;
      }}
    />
  );
};

ModuleRoute.propTypes = {
  module: PropTypes.string.isRequired,
  parentPermissions: PropTypes.arrayOf(PropTypes.string),
  component: PropTypes.elementType.isRequired,
};

const PermissionRoute = ({ requiredPermissions, settings, component: Component, ...rest }) => {
  const permissions = useSelector((state) => state.user?.current?.permissions) || [];
  const accessGranted = settings ? hasAnySettingsAccess(permissions) : hasAnyPermission(permissions, requiredPermissions);

  return (
    <Route
      {...rest}
      render={(routeProps) => (accessGranted ? <Component {...routeProps} /> : <Redirect to="/modules" />)}
    />
  );
};

PermissionRoute.propTypes = {
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  settings: PropTypes.bool,
  component: PropTypes.elementType.isRequired,
};

/**
 * `/` belongs to neither app in particular. A verified session goes to the
 * module picker; anything else is handed to the SPA's login, as a document
 * request because that route is not this app's.
 */
const RootRedirect = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) return <Redirect to="/modules" />;

  goToLogin({ replace: true });
  return null;
};

const EntryPage = () => {
  useEffect(() => {
    // window.history.replaceState(null, "", window.location.pathname + window.location.hash);
  }, []);

  return (
    <AuthProvider>
      <LocationSessionProvider>
        <LocationSyncModal />
        <MaintenanceGate>
          <ModalsProvider>
            <Switch>
              <Route exact path="/" component={RootRedirect} />
              <Route path="/onboarding" component={OnboardingRoutes} />
              <Route path="/modules" component={ModuleRoutes} />
              <PermissionRoute path="/settings" settings component={SettingsRoutes} />
              <ModuleRoute
                path="/inventory"
                module={MODULE_IDS.INVENTORY}
                parentPermissions={CENTRAL_STORE_ACCESS}
                component={InventoryRoutes}
              />
              <ModuleRoute path="/laboratory" module={MODULE_IDS.LABORATORY} component={LaboratoryRoutes} />
              <PermissionRoute path="/audit-trail" requiredPermissions={AUDIT_TRAIL_ACCESS} component={AuditTrailRoutes} />
              <ModuleRoute path="/frontdesk" module={MODULE_IDS.FRONTDESK} component={FrontdeskRoutes} />
              <ModuleRoute path="/in-patient" module={MODULE_IDS.IN_PATIENT} component={PatientRoutes} />
              <ModuleRoute path="/out-patient" module={MODULE_IDS.OUT_PATIENT} component={PatientRoutes} />
              <ModuleRoute path="/pharmacy" module={MODULE_IDS.PHARMACY} component={PharmacyRoutes} />
              <ModuleRoute path="/billing" module={MODULE_IDS.BILLING} component={FinanceRoutes} />
              <ModuleRoute
                path="/report"
                module={MODULE_IDS.REPORT}
                parentPermissions={REPORT_ACCESS}
                component={ReportingRoutes}
              />
              <ModuleRoute
                path="/subscriptions"
                module={MODULE_IDS.SUBSCRIPTIONS}
                parentPermissions={SUBSCRIPTIONS_ACCESS}
                component={SubscriptionRoutes}
              />
              <Route path="/" component={PublicRoute} />
            </Switch>
          </ModalsProvider>
        </MaintenanceGate>
      </LocationSessionProvider>
    </AuthProvider>
  );
};

export default EntryPage;
